import * as React from "react";
import { DataLayer } from "@grandvisionhq/analytics";
import { useGoogleMapsContext, MediaQuery, InfoWindow } from "@grandvisionhq/common";
import { LocationSearchMap } from "@grandvisionhq/locations";
import { useIntl } from "@grandvisionhq/state";
import { useStoreFinderContext, Components } from "@grandvisionhq/stores-v2";
import { aabPreferredStoreClickEvent } from "../../../../../../../data-layer/v3/data-layer/actions.js";
import { fromStoreResultToStore } from "../../../../../../../pages/BookAppointment/adapters/v3/pages/BookAppointment/adapters/from-store-result-to-store.js";
import StoreFinderItemContainer from "../../../../../StoreFinderItem/v3/components/StoreFinder/StoreFinderItem/StoreFinderItemContainer.js";
const CurrentLocationPin = Components.CurrentLocationPin;
const currentLocationPin = CurrentLocationPin({
  theme: { borderColor: "#2B2E31", background: "#FFFFFF" }
});
const PreferredStorePin = Components.PreferredStorePin;
const preferredStorePinFallback = PreferredStorePin();
const StoreFinderMapContainer = ({ stores, googleMapsId }) => {
  const {
    hoveredStore: hoveredStoreInList,
    isUsingUserLocation,
    userLocation,
    isUserLocationRequested,
    setIsUserLocationRequested,
    preferredStore,
    activeMapInfoWindow,
    setActiveMapInfoWindow
  } = useStoreFinderContext();
  const {
    googleMapsSettings: { preferredStorePin }
  } = useGoogleMapsContext();
  const { getLabel } = useIntl();
  const [hoveredStore, setHoveredStore] = React.useState(null);
  const handleOnMarkerHover = (markerId) => {
    setHoveredStore(markerId);
  };
  const isPhone = MediaQuery.useIsPhone();
  const [activeStore, setActiveStore] = React.useState(null);
  const handleOnClickUseMyLocation = React.useCallback(() => {
    if (!isUserLocationRequested) {
      setIsUserLocationRequested(true);
    }
  }, [isUserLocationRequested, setIsUserLocationRequested]);
  const handleOnMarkerClick = React.useCallback(
    (markerId) => {
      if (activeMapInfoWindow === markerId) {
        setActiveMapInfoWindow(null);
        setActiveStore(null);
        return;
      }
      const storesWithPreferredStore = preferredStore ? [...stores, preferredStore] : stores;
      const store = storesWithPreferredStore.find((store2) => store2.globalStoreId === markerId);
      if (store) {
        setActiveMapInfoWindow(markerId);
        setActiveStore(fromStoreResultToStore(store));
      }
    },
    [activeMapInfoWindow, preferredStore, stores, setActiveMapInfoWindow]
  );
  const handleOnMapClick = React.useCallback(() => {
    setActiveMapInfoWindow(null);
  }, [setActiveMapInfoWindow]);
  const handleOnClickPreferredStoreItem = (store) => {
    DataLayer.pushEvent(
      aabPreferredStoreClickEvent({
        store,
        itemType: "map"
      })
    );
  };
  const preferredStoreMarkerConfig = React.useCallback(
    (preferredStore2, excludedFromMapBehavior = true) => {
      return {
        markerId: preferredStore2.globalStoreId,
        title: getLabel("appointments.component.storeFinderMap.map.pin.title"),
        position: { lat: Number(preferredStore2.lat), lng: Number(preferredStore2.lon) },
        pin: preferredStorePin || preferredStorePinFallback,
        infoWindow: activeMapInfoWindow === preferredStore2.globalStoreId && !isPhone && /* @__PURE__ */ React.createElement(
          InfoWindow,
          {
            position: { lat: Number(preferredStore2.lat), lng: Number(preferredStore2.lon) },
            onCloseClick: handleOnMapClick
          },
          /* @__PURE__ */ React.createElement(
            StoreFinderItemContainer,
            {
              variant: "map-item",
              store: fromStoreResultToStore(preferredStore2),
              "data-t": "searchResultTile",
              onClick: handleOnClickPreferredStoreItem
            }
          )
        ),
        excludedFromMapBehavior
      };
    },
    [activeMapInfoWindow, getLabel, handleOnMapClick, isPhone, preferredStorePin]
  );
  const storeMarkers = React.useMemo(() => {
    return stores.map((store) => {
      const { globalStoreId, lat, lon } = store;
      const storeIsPreferredStore = store.globalStoreId === preferredStore?.globalStoreId;
      if (storeIsPreferredStore) {
        return preferredStoreMarkerConfig(store, false);
      }
      return {
        markerId: globalStoreId.toString(),
        title: getLabel("appointments.component.storeFinderMap.map.pin.title"),
        position: {
          lat: Number(lat),
          lng: Number(lon)
        },
        infoWindow: activeMapInfoWindow === globalStoreId.toString() && !isPhone && /* @__PURE__ */ React.createElement(
          InfoWindow,
          {
            position: { lat: Number(lat), lng: Number(lon) },
            onCloseClick: handleOnMapClick
          },
          /* @__PURE__ */ React.createElement(
            StoreFinderItemContainer,
            {
              variant: "map-item",
              store: fromStoreResultToStore(store),
              "data-t": "searchResultTile"
            }
          )
        )
      };
    });
  }, [
    activeMapInfoWindow,
    getLabel,
    handleOnMapClick,
    isPhone,
    preferredStore?.globalStoreId,
    preferredStoreMarkerConfig,
    stores
  ]);
  const userLocationMarker = React.useMemo(() => {
    if (isUsingUserLocation && userLocation) {
      return [
        {
          position: { lat: userLocation.lat, lng: userLocation.lng },
          pin: currentLocationPin
        }
      ];
    }
    return [];
  }, [isUsingUserLocation, userLocation]);
  const preferredStoreMarker = React.useMemo(() => {
    const preferredStoreFoundInList = !!stores.filter(
      (store) => store.globalStoreId === preferredStore?.globalStoreId
    ).length;
    if (preferredStore && !preferredStoreFoundInList) {
      return [preferredStoreMarkerConfig(preferredStore)];
    }
    return [];
  }, [preferredStore, preferredStoreMarkerConfig, stores]);
  const markers = React.useMemo(() => {
    return [...storeMarkers, ...userLocationMarker, ...preferredStoreMarker];
  }, [preferredStoreMarker, storeMarkers, userLocationMarker]);
  const center = React.useMemo(() => {
    if (isUsingUserLocation && userLocation !== void 0) {
      return userLocation;
    }
    return void 0;
  }, [userLocation, isUsingUserLocation]);
  return /* @__PURE__ */ React.createElement(
    LocationSearchMap,
    {
      id: googleMapsId,
      markers,
      markersUpdateConfig: {
        centerToFirst: true,
        zoom: 13
      },
      center,
      highlightMarkerId: activeStore?.globalStoreId ?? null,
      hovered: hoveredStore ?? hoveredStoreInList?.globalStoreId,
      userLocation: isUsingUserLocation && userLocation ? userLocation : void 0,
      userLocationZoomLevel: 13,
      centerOnUserLocation: true,
      activeLocation: activeStore && isPhone ? /* @__PURE__ */ React.createElement(StoreFinderItemContainer, { variant: "map-item", store: activeStore }) : null,
      onClickUseMyLocation: handleOnClickUseMyLocation,
      onMarkerHover: handleOnMarkerHover,
      onMarkerClick: handleOnMarkerClick,
      onMapClick: handleOnMapClick
    }
  );
};
export {
  StoreFinderMapContainer as default
};
//# sourceMappingURL=StoreFinderMapContainer.js.map
